import { useState, useEffect, useCallback, useRef } from 'react';
import { Editor } from '@tiptap/react';
import { getImprovedText } from '../../../services/chatMessageUtilService';
import { isEmpty } from '../../../helpers/common';
import { captureException } from '../../../services/logService';
import { rootStore } from '../../../mobx/rootStore';

export function useEditorImprove(editor: Editor | null) {
  const [isImproveModalOpen, setImproveModalOpen] = useState(false);
  const [isImproveLoading, setIsImproveLoading] = useState(false);
  const [improvedText, setImprovedText] = useState('');
  const improveModalRef = useRef<HTMLDivElement>(null);

  const setImproveModel = useCallback(
    async (e?: React.MouseEvent) => {
      e?.stopPropagation();
      e?.preventDefault();

      if (editor) {
        try {
          setIsImproveLoading(true);

          const { state } = editor;
          const { from, to } = state.selection;
          const selectedText = state.doc.textBetween(from, to, ' ');
          if (isEmpty(selectedText.trim())) {
            rootStore.alertNotificationStore.setErrorMessage('selected message cannot be empty');
            return;
          }
          editor.commands.focus();
          setImproveModalOpen(true);
          const text = await getImprovedText(selectedText);
          setImprovedText(text);
        } catch (error) {
          captureException(error);
        } finally {
          setIsImproveLoading(false);
        }
      }
    },
    [editor]
  );

  const onClickReplace = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      e?.preventDefault();

      if (editor && improvedText) {
        const { state } = editor;
        const { from, to } = state.selection;
        editor.commands.insertContentAt({ from, to }, improvedText);
        editor.commands.focus();
      }
      setImproveModalOpen(false);
    },
    [editor, improvedText]
  );

  // Calculate improve modal position based on available viewport space
  useEffect(() => {
    if (!isImproveModalOpen || !improveModalRef.current || !editor) return;

    const positionModal = () => {
      const modal = improveModalRef.current;
      if (!modal) return;

      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const padding = 8;
      const isMobileScreen = viewportWidth <= 449;

      const { from, to } = editor.state.selection;
      let startCoords, endCoords;
      try {
        startCoords = editor.view.coordsAtPos(from);
        endCoords = editor.view.coordsAtPos(to);
      } catch {
        return;
      }
      const anchorTop = Math.min(startCoords.top, endCoords.top);
      const anchorBottom = Math.max(startCoords.bottom, endCoords.bottom);
      const anchorLeft = Math.min(startCoords.left, endCoords.left);

      const modalRect = modal.getBoundingClientRect();
      const modalHeight = modalRect.height;

      const spaceAbove = anchorTop;
      const spaceBelow = viewportHeight - anchorBottom;

      let topPos: number;
      if (spaceBelow >= modalHeight + padding) {
        topPos = anchorBottom + padding;
      } else if (spaceAbove >= modalHeight + padding) {
        topPos = anchorTop - modalHeight - padding;
      } else if (spaceBelow >= spaceAbove) {
        topPos = anchorBottom + padding;
      } else {
        topPos = anchorTop - modalHeight - padding;
      }

      topPos = Math.max(padding, Math.min(topPos, viewportHeight - modalHeight - padding));
      modal.style.top = `${topPos}px`;

      if (isMobileScreen) {
        const modalWidth = Math.min(modalRect.width, viewportWidth - padding * 2);
        modal.style.left = `${Math.max(padding, (viewportWidth - modalWidth) / 2)}px`;
        modal.style.width = `${modalWidth}px`;
      } else {
        let leftPos = anchorLeft;
        if (leftPos + modalRect.width > viewportWidth - padding) {
          leftPos = viewportWidth - modalRect.width - padding;
        }
        if (leftPos < padding) {
          leftPos = padding;
        }
        modal.style.left = `${leftPos}px`;
      }
    };

    requestAnimationFrame(positionModal);
    window.addEventListener('resize', positionModal);
    return () => {
      window.removeEventListener('resize', positionModal);
    };
  }, [isImproveModalOpen, improvedText, editor]);

  return {
    isImproveModalOpen,
    setImproveModalOpen,
    isImproveLoading,
    improvedText,
    improveModalRef,
    setImproveModel,
    onClickReplace
  };
}
